// src/components/admin/AdminSidebar.jsx

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Tooltip,
  Typography,
  Fab,
  alpha,
  Collapse,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import DashboardIcon from '@mui/icons-material/SpaceDashboard';
import PeopleIcon from '@mui/icons-material/PeopleAlt';
import ShieldIcon from '@mui/icons-material/AdminPanelSettings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LaptopIcon from '@mui/icons-material/Devices';
import LocationOnIcon from '@mui/icons-material/SwapHoriz';
import NotificationsIcon from '@mui/icons-material/NotificationsActive';
import AssessmentIcon from '@mui/icons-material/InsertChart';
import BuildIcon from '@mui/icons-material/SupportAgent';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FeedbackIcon from '@mui/icons-material/RateReview';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import HowToRegIcon from '@mui/icons-material/HowToReg';

import LogoutConfirmation from '../../components/Modals/LogoutConfirmation';
import { usePendingApprovalsCount } from '../../hooks/usePendingApprovalsCount';

const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 76;

const buildNavSections = (myApprovalsItem) => {
  const sections = [
    {
      title: 'Main',
      items: [
        {
          path: '/admin/dashboard',
          key: 'dashboard',
          label: 'Overview',
          icon: <DashboardIcon />,
        },
      ],
    },
    {
      title: 'Management',
      items: [
        {
          path: '/admin/users',
          key: 'users',
          label: 'Users',
          icon: <PeopleIcon />,
        },
        {
          path: '/admin/access-requests',
          key: 'accessRequests',
          label: 'Permissions',
          icon: <ShieldIcon />,
        },
        {
          path: '/admin/master-data',
          key: 'masterdata',
          label: 'Master Data',
          icon: <AccountTreeIcon />,
        },
        {
          path: '/admin/audit',
          key: 'audit',
          label: 'Audit Events',
          icon: <FactCheckIcon />,
        },
      ],
    },
    {
      title: 'Assets',
      items: [
        {
          path: '/admin/assets',
          key: 'assets',
          label: 'Assets',
          icon: <LaptopIcon />,
        },
        {
          path: '/admin/change-location',
          key: 'changeLocation',
          label: 'Move Assets',
          icon: <LocationOnIcon />,
        },
      ],
    },
    {
      title: 'Insights',
      items: [
       
        {
          key: 'reports',
          label: 'Reports',
          icon: <AssessmentIcon />,
          subItems: [
            { path: '/admin/reports/assetRegistry', key: 'assetRegistry', label: 'Asset Registry' },
            { path: '/admin/reports/assetAuditVerification', key: 'assetAuditVerification', label: 'Audit & Verification' },
            { path: '/admin/reports/unverifiedAssets', key: 'unverifiedAssets', label: 'Unverified Assets' },
            { path: '/admin/reports/missingAssets', key: 'missingAssets', label: 'Missing Assets' },
            { path: '/admin/reports/assetMovement', key: 'assetMovement', label: 'Transfer History' },
            { path: '/admin/reports/depreciationRegister', key: 'depreciationRegister', label: 'Depreciation Register' },
            { path: '/admin/reports/consumableStock', key: 'consumableStock', label: 'Consumable Stock' },
            { path: '/admin/reports/expiringAssets', key: 'expiringAssets', label: 'Expiring Assets' },
            { path: '/admin/reports/expiredAssets', key: 'expiredAssets', label: 'Expired Assets' },
          ],
        },
      ],
    },
    {
      title: 'Support',
      items: [
        { path: '/admin/help-desk', key: 'helpdesk', label: 'Support', icon: <BuildIcon /> },
        { path: '/admin/knowledge-base', key: 'knowledgeBase', label: 'Help Center', icon: <MenuBookIcon /> },
        { path: '/admin/feedback', key: 'feedback', label: 'Feedback', icon: <FeedbackIcon /> },
        { path: '/admin/settings', key: 'settings', label: 'Settings', icon: <SettingsIcon /> },
      ],
    },
  ];

  if (myApprovalsItem) {
    const assetsIdx = sections.findIndex((s) => s.title === 'Assets');
    if (assetsIdx !== -1) {
      sections[assetsIdx] = {
        ...sections[assetsIdx],
        items: [...sections[assetsIdx].items, myApprovalsItem],
      };
    }
  }

  return sections;
};

const BRAND_PRIMARY = '#1e40af';
const BRAND_LIGHT = '#eff6ff';

const AdminSidebar = ({ logout, isOpen, isMobile, onSidebarToggle, currentUser }) => {
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  
  // Track open state for submenus
  const [openMenus, setOpenMenus] = useState({});

  // Pending approvals badge (universal — any authenticated user)
  const { count: pendingApprovalsCount, userIsApprover } =
    usePendingApprovalsCount(currentUser, location.pathname);

  const myApprovalsItem = userIsApprover
    ? {
        path: '/admin/my-approvals',
        key: 'myApprovals',
        label: 'My Approvals',
        icon: <HowToRegIcon />,
        badgeCount: pendingApprovalsCount,
      }
    : null;

  const NAV_SECTIONS = buildNavSections(myApprovalsItem);

  // Auto-expand submenu if a child is active on load
  useEffect(() => {
    NAV_SECTIONS.forEach((section) => {
      section.items.forEach((item) => {
        if (item.subItems) {
          const isChildActive = item.subItems.some((sub) => location.pathname.startsWith(sub.path));
          if (isChildActive) {
            setOpenMenus((prev) => ({ ...prev, [item.key]: true }));
          }
        }
      });
    });
  }, [location.pathname]);

  const getActiveKey = () => {
    const allItems = [];
    NAV_SECTIONS.forEach((s) => {
      s.items.forEach((i) => {
        if (i.subItems) {
          i.subItems.forEach((sub) => allItems.push(sub));
        } else {
          allItems.push(i);
        }
      });
    });
    // Sort by path length descending to match most specific route first
    allItems.sort((a, b) => b.path.length - a.path.length);
    const matched = allItems.find((item) => location.pathname.startsWith(item.path));
    return matched ? matched.key : '';
  };

  const activeKey = getActiveKey();
  const currentWidth = isOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  const handleToggle = () => onSidebarToggle(!isOpen);

  const handleNavClick = () => {
    if (isMobile) onSidebarToggle(false);
  };

  const handleMenuToggle = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
    if (!isOpen) onSidebarToggle(true); // Open sidebar if clicking closed parent
  };

  const handleLogoutClick = () => setShowLogoutModal(true);

  const handleLogoutConfirm = async () => {
    setLogoutLoading(true);
    try {
      await logout();
    } catch (e) {
      console.error(e);
    } finally {
      setLogoutLoading(false);
      setShowLogoutModal(false);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOpen ? 'space-between' : 'center',
          gap: 1,
          px: isOpen ? 1.5 : 1,
          py: 1.5,
          minHeight: 72,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: alpha('#000', 0.06),
          overflow: 'hidden',
        }}
      >
        {isOpen ? (
          <>
            <Box
              component={Link}
              to="/admin/dashboard"
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                textDecoration: 'none',
                cursor: 'pointer',
                borderRadius: 1,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(BRAND_PRIMARY, 0.04),
                },
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/default-logo.png';
                }}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
            <IconButton
              onClick={handleToggle}
              size="small"
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(BRAND_PRIMARY, 0.08),
                  color: BRAND_PRIMARY,
                },
              }}
            >
              <MenuOpenIcon />
            </IconButton>
          </>
        ) : (
          <IconButton
            onClick={handleToggle}
            sx={{
              color: BRAND_PRIMARY,
              backgroundColor: BRAND_LIGHT,
              '&:hover': { backgroundColor: alpha(BRAND_PRIMARY, 0.15) },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* ── NAV ── */}
      <Box
        component="nav"
        sx={{
          flex: '1 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          px: 1.25,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#000', 0.12),
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
        }}
      >
        {NAV_SECTIONS.map((section, index) => (
          <Box
            key={section.title}
            sx={{ mb: index === NAV_SECTIONS.length - 1 ? 0 : 1.25 }}
          >
            {isOpen && (
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'block',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  opacity: 0.7,
                }}
              >
                {section.title}
              </Typography>
            )}

            <List disablePadding>
                            {section.items.map((item) => {
                const { path, key, label, icon, subItems, badgeCount } = item;
                const hasSub = Boolean(subItems);
                // Parent is active if activeKey matches OR if a subItem matches
                const isSubActive = hasSub && subItems.some((s) => s.key === activeKey);
                const isActive = activeKey === key || isSubActive;
                const isMenuOpen = openMenus[key];

                return (
                  <React.Fragment key={key}>
                    <ListItem disablePadding sx={{ mb: 0.25 }}>
                      <Tooltip title={!isOpen ? label : ''} placement="right" arrow>
                        <ListItemButton
                          component={hasSub ? 'div' : Link}
                          to={hasSub ? undefined : path}
                          onClick={(e) => {
                            if (hasSub) {
                              e.preventDefault();
                              handleMenuToggle(key);
                            } else {
                              handleNavClick();
                            }
                          }}
                          sx={{
                            minHeight: 44,
                            borderRadius: '10px',
                            justifyContent: isOpen ? 'flex-start' : 'center',
                            px: isOpen ? 1.5 : 0,
                            mx: isOpen ? 0 : 'auto',
                            width: isOpen ? '100%' : 48,
                            position: 'relative',
                            backgroundColor: isActive ? BRAND_LIGHT : 'transparent',
                            color: isActive ? BRAND_PRIMARY : 'text.primary',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: isActive
                                ? BRAND_LIGHT
                                : alpha('#000', 0.04),
                            },
                            '&::before': isActive
                              ? {
                                  content: '""',
                                  position: 'absolute',
                                  left: isOpen ? -6 : -12,
                                  top: '20%',
                                  bottom: '20%',
                                  width: 3,
                                  borderRadius: '0 4px 4px 0',
                                  backgroundColor: BRAND_PRIMARY,
                                }
                              : {},
                          }}
                        >
                          {icon && (
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: isOpen ? 1.75 : 0,
                                justifyContent: 'center',
                                color: isActive ? BRAND_PRIMARY : 'text.secondary',
                                '& svg': { fontSize: '1.35rem' },
                              }}
                            >
                              {icon}
                            </ListItemIcon>
                          )}
                                                 {isOpen && (
                            <ListItemText
                              primary={label}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: isActive ? 600 : 500,
                                noWrap: true,
                              }}
                            />
                          )}

                          {/* Badge chip (expanded sidebar) */}
                          {badgeCount > 0 && isOpen && (
                            <Box
                              sx={{
                                ml: 1,
                                minWidth: 20,
                                height: 20,
                                px: 0.75,
                                borderRadius: 10,
                                bgcolor: isActive ? BRAND_PRIMARY : '#dc2626',
                                color: '#fff',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {badgeCount > 99 ? '99+' : badgeCount}
                            </Box>
                          )}

                          {/* Red dot (collapsed sidebar) */}
                          {badgeCount > 0 && !isOpen && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#dc2626',
                              }}
                            />
                          )}

                          {isOpen && hasSub && (
                            isMenuOpen ? <ExpandLessIcon sx={{ fontSize: '1.2rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1.2rem' }} />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>

                    {/* SUB-ITEMS COLLAPSE */}
                    {hasSub && (
                      <Collapse in={isMenuOpen && isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ mb: 1 }}>
                          {subItems.map((sub) => {
                            const isChildActive = activeKey === sub.key;
                            return (
                              <ListItemButton
                                key={sub.key}
                                component={Link}
                                to={sub.path}
                                onClick={handleNavClick}
                                sx={{
                                  pl: 4.5,
                                  pr: 2,
                                  py: 0.75,
                                  minHeight: 36,
                                  color: isChildActive ? BRAND_PRIMARY : 'text.secondary',
                                  backgroundColor: 'transparent',
                                  borderRadius: '0 8px 8px 0',
                                  mr: 1.5,
                                  '&:hover': {
                                    color: BRAND_PRIMARY,
                                    backgroundColor: alpha(BRAND_PRIMARY, 0.04),
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 22, color: 'inherit' }}>
                                  <ArrowRightIcon sx={{ fontSize: '1.2rem5', ml: -0.5 }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={sub.label} 
                                  primaryTypographyProps={{ 
                                    fontSize: '0.8125rem',
                                    fontWeight: isChildActive ? 600 : 400
                                  }} 
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* ── FOOTER / LOGOUT ── */}
      <Box
        sx={{
          p: 1.25,
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: alpha('#000', 0.06),
        }}
      >
        <Tooltip title={!isOpen ? 'Logout' : ''} placement="right" arrow>
          <ListItemButton
            onClick={handleLogoutClick}
            sx={{
              minHeight: 44,
              borderRadius: '10px',
              justifyContent: isOpen ? 'flex-start' : 'center',
              px: isOpen ? 1.5 : 0,
              mx: isOpen ? 0 : 'auto',
              width: isOpen ? '100%' : 48,
              color: '#dc2626',
              '&:hover': { backgroundColor: alpha('#dc2626', 0.08) },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isOpen ? 1.75 : 0,
                justifyContent: 'center',
                color: '#dc2626',
                '& svg': { fontSize: '1.35rem' },
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile && !isOpen && (
        <Fab
          size="medium"
          onClick={handleToggle}
          aria-label="Open sidebar"
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            backgroundColor: BRAND_PRIMARY,
            color: '#fff',
            boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
            '&:hover': { backgroundColor: '#1e3a8a' },
          }}
        >
          <MenuIcon />
        </Fab>
      )}

      {!isMobile && (
        <Drawer
          variant="permanent"
          open={isOpen}
          PaperProps={{
            sx: {
              width: currentWidth,
              transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
              border: 'none',
              boxShadow: '0 0 20px rgba(0,0,0,0.04)',
              boxSizing: 'border-box',
            },
          }}
          sx={{
            width: currentWidth,
            flexShrink: 0,
            transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiDrawer-paper': {
              width: currentWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {isMobile && (
        <Drawer
          variant="temporary"
          open={isOpen}
          onClose={() => onSidebarToggle(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {showLogoutModal && (
        <LogoutConfirmation
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogoutConfirm}
          loading={logoutLoading}
        />
      )}
    </>
  );
};

export default AdminSidebar;